"use client";

import { DEFAULT_LOGIN_URL, INTERNAL_ROUTES } from "@/constants";
import { useAuth } from "@/hooks";
import { usePathname, useRouter } from "next/navigation";
import PageLoading from "../shared/page-loading";
import React, { useState } from "react";

type AuthContextProps = {
  username: string | undefined;
  setUsername: (username: string | undefined) => void;
  email: string | undefined;
  setEmail: (email: string | undefined) => void;
  allowedScreens: string[] | undefined;
  setAllowedScreens: (allowedScreens: string[] | undefined) => void;
  userRole: string | undefined;
  setUserRole: (userRole: string | undefined) => void;
};

const AuthContext = React.createContext<AuthContextProps | null>(null);

export function useAuthContext() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}

// This provider is used to provide authentication to our app
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [allowedScreens, setAllowedScreens] = useState<string[] | undefined>(undefined);
  const [userRole, setUserRole] = useState<string | undefined>(undefined);

  if (isLoading) {
    return <PageLoading description="Authorizing..." />;
  }

  return (
    <AuthContext.Provider
      value={{
        allowedScreens,
        email,
        username,
        userRole,
        setAllowedScreens,
        setEmail,
        setUsername,
        setUserRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );

  // Because we have logic to check if user's not logged in, we will redirect to login page
  // We need to handle the case when user is logged in but doesn't have permission to access the page
  // In this case, we will show the unauthorized page
  // If we dont check pathname, it will jump into the case (Authen failed) below -> it's redirecting to login page -> back to authorized page -> back to login page -> looping issue
  if (pathname === INTERNAL_ROUTES.UNAUTHORIZED) {
    // If not authenticated, show authorized page
    if (!isAuthenticated && !isLoading) {
      return <>{children}</>;
    }

    // If authenticated, redirect to account management
    if (isAuthenticated && !isLoading) {
      router.replace(INTERNAL_ROUTES.HOME);
      return;
    }
  }

  // If authenticated, show children
  if (isAuthenticated && !isLoading) {
    if (pathname === INTERNAL_ROUTES.LOGIN) {
      router.replace(INTERNAL_ROUTES.HOME);
      return;
    }
    return <>{children}</>;
  }

  // (Authen failed) if not authenticated, redirect to SSO for login
  if (!isAuthenticated && !isLoading) {
    window.location.href = DEFAULT_LOGIN_URL;
    return <PageLoading description="Redirecting to login..." />;
  }

  // We need to call get me first to check if user is authenticated
  // If calling api, show loading
  return <PageLoading description="Authorizing..." />;
}
